import { useState, useEffect } from "react";
import {
  IconButton,
  TextField,
  FormControl,
  Box,
  Typography,
  Select,
  MenuItem,
  Autocomplete,
  Stack,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useGetData } from "../../hooks";
import { baseUrl } from "../../_constants";
import axios from "axios";

export default function ImagesFilters({
  resetFilters,
  filters,
  setFilters,
  search = true,
  defaultFiltersOpen,
  setPhotos, // Add this prop to pass the filtered photos to the parent component,
  setTotalPages, // Add this prop to update the total pages in the parent component
}) {
  const [filtersVisible, setFiltersVisible] = useState(defaultFiltersOpen);
  const { data: plants } = useGetData("plants");
  const { data: yards } = useGetData("yards");
  const [loading, setLoading] = useState(false);

  // Function to fetch plant photos based on current filters
  const fetchPlantPhotos = async () => {
    try {
      setLoading(true);
      // Set default plantId and yard for the API call
      let plantId = filters.plantId || "%2A";

      if (plantId === "%2A/%2A") {
        plantId = "%2A";
      }
      const yard = filters.yard || "%2A";

      console.log("Filters:>> ", filters);

      // Format dates for the API call if they exist
      let startDateParam = "";
      let endDateParam = "";

      if (filters.startDate) {
        startDateParam = `/${filters.startDate.toISOString()}`;
      }

      if (filters.endDate) {
        endDateParam = `/${filters.endDate.toISOString()}`;
      }

      // if the startDate is defined and the endDate is not, set the endDate to today
      if (filters.startDate && !filters.endDate) {
        endDateParam = `/${new Date().toISOString()}`;
      }

      // Build the API endpoint with query parameters
      const api = `${baseUrl}/plant-photos/${plantId}/${yard}${startDateParam}${endDateParam}`;
      const response = await axios.get(api);

      // Process and filter the response based on date range and sort
      if (response.data && response.data.data) {
        let processedPhotos = response.data.data.flatMap(photoObj => {
          // Extract the key and data from each object
          const entries = Object.entries(photoObj);
          if (entries.length === 0) return [];

          const [id, photoData] = entries[0];
          return {
            id,
            ...photoData
          };
        });

        // Filter by date range if specified - this filtering is now handled by the server
        // but keeping client-side filtering as a backup or for additional refinement
        if (filters.startDate) {
          processedPhotos = processedPhotos.filter(photo =>
            new Date(photo.createdDate) >= new Date(filters.startDate)
          );
        }

        if (filters.endDate) {
          processedPhotos = processedPhotos.filter(photo =>
            new Date(photo.createdDate) <= new Date(filters.endDate)
          );
        }

        // Filter out photos before December 2023 if that option is checked
        if (filters.hideBeforeDecember2023) {
          const dec2023 = new Date('2023-12-31T00:00:00.000Z');
          processedPhotos = processedPhotos.filter(photo =>
            new Date(photo.createdDate) > dec2023
          );
        }

        // Sort photos based on the sort option
        if (filters.sort === "newest") {
          processedPhotos.sort((a, b) =>
            new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
          );
        } else if (filters.sort === "latest") {
          processedPhotos.sort((a, b) =>
            new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
          );
        }

        // Pass the processed photos to the parent component
        console.log("Processed Photos:", processedPhotos);
        setPhotos(processedPhotos); // set the filtered photos in the parent component

        // Check if metadata exists and contains totalCount
        if (response.data.metadata && response.data.metadata.totalCount !== undefined) {
          setTotalPages(Math.ceil(response.data.metadata.totalCount / 9)); // set the total pages in the parent component
        } else {
          setTotalPages(Math.ceil(processedPhotos.length / 9)); // fallback to client-side calculation
        }
      }
    } catch (error) {
      console.error("Error fetching plant photos:", error);
      setPhotos([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Effect to trigger fetch when filters change
  useEffect(() => {
    fetchPlantPhotos();
  }, [filters]);

  const toggleFiltersVisible = () => setFiltersVisible(!filtersVisible);

  const onAutocompleteChange = (_, value) => {
    if (!value) return;
    setFilters(prevFilters => ({
      ...prevFilters,
      plantId: value.id,
      hideBeforeDecember2023: true
    }));
  };

  const onDateChange = (key) => (date) =>
    setFilters((oldFilters) => ({ ...oldFilters, [key]: date }));

  const onYardChange = (e) =>
    setFilters((oldFilters) => ({ ...oldFilters, yard: e.target.value }));

  const onSortChange = (e) =>
    setFilters((oldFilters) => ({ ...oldFilters, sort: e.target.value }));

  const onHideBeforeDecember2023Change = (e) =>
    setFilters((oldFilters) => ({
      ...oldFilters,
      hideBeforeDecember2023: e.target.checked,
    }));

  return (
    <>
      {search && (
        <div style={{ display: "flex" }}>
          <IconButton sx={{ p: "10px" }} aria-label="search">
            <SearchIcon />
          </IconButton>
          <Autocomplete
            noOptionsText={"No more options"}
            variant="standard"
            id="search2"
            options={plants}
            style={{ marginTop: "-14px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for a plant in our photo library"
                variant="standard"
                sx={{
                  "& .MuiInputBase-input": { fontFamily: 'Lato' },
                  "& .MuiInputLabel-root": { fontFamily: 'Lato' },
                }}
              />
            )}
            getOptionLabel={(option) => `${option.id} ${" - "} ${option.name}`}
            fullWidth
            onChange={onAutocompleteChange}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "clear") {
                resetFilters();
              }
            }}
          />
          <IconButton onClick={toggleFiltersVisible} sx={{ p: "10px" }}>
            <FilterAltIcon />
          </IconButton>
        </div>
      )}
      <div className={`${filtersVisible ? "show" : "hide"} bottom-section`}>
        {!defaultFiltersOpen && (
          <IconButton
            fontSize="small"
            style={{ float: "right" }}
            onClick={toggleFiltersVisible}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Box display="flex" justifyContent="space-between">
          <FormControl
            variant="outlined"
            sx={{
              m: 2,
              mt: defaultFiltersOpen ? 2 : 8,
              minWidth: 150,
              height: 30,
              color: "black",
            }}
          >
            <Typography component="div">
              <Box sx={{ fontStyle: "italic", m: 0.5 }}>Sort By</Box>
            </Typography>
            <Select
              fullWidth
              id="filter-sortby-select"
              displayEmpty
              value={filters.sort}
              onChange={onSortChange}
              sx={{ fontFamily: 'Lato' }}
            >
              <MenuItem sx={{ fontFamily: 'Lato' }} value="newest">Newest First</MenuItem>
              <MenuItem sx={{ fontFamily: 'Lato' }} value="latest">Oldest First</MenuItem>
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            sx={{
              mr: 2,
              mt: defaultFiltersOpen ? 2 : 8,
              minWidth: 150,
              height: 30,
            }}
          >
            <Typography component="div">
              <Box sx={{ fontStyle: "italic", m: 0.5 }}>Yard</Box>
            </Typography>
            <Select
              fullWidth
              displayEmpty
              id="filter-yard-select"
              value={filters.yard}
              onChange={onYardChange}
              sx={{ fontFamily: 'Lato' }}
            >
              <MenuItem value="" sx={{ fontFamily: 'Lato' }}>
                <em>All Yards</em>
              </MenuItem>
              {yards?.map((yard, index) => (
                <MenuItem key={`yard-${yard}-${index}`} value={yard.name} sx={{ fontFamily: 'Lato' }}>
                  {yard.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            sx={{
              mb: 2,
              mt: defaultFiltersOpen ? 2 : 8,
              mr: defaultFiltersOpen ? 2 : 0,
              minWidth: 350,
            }}
          >
            <Typography component="div">
              <Box sx={{ fontStyle: "italic", m: 0.5, fontFamily: 'Lato' }}>Date Range</Box>
            </Typography>
            <Stack direction="row" spacing={2}>
              <DatePicker
                label="Start Date"
                value={filters.startDate}
                maxDate={filters.endDate}
                onChange={onDateChange("startDate")}
                renderInput={(params) => <TextField {...params} sx={{ fontFamily: 'Lato' }} />}
              />

              <DatePicker
                label="End Date"
                value={filters.endDate}
                minDate={filters.startDate}
                onChange={onDateChange("endDate")}
                renderInput={(params) => <TextField {...params} sx={{ fontFamily: 'Lato' }} />}
              />
            </Stack>
          </FormControl>
        </Box>
        <Box>
          <FormControl
            variant="outlined"
            style={{ display: "flex", alignItems: "start", padding: "20px", marginTop: "0px" }}
            sx={{
              mb: 2,
              mt: defaultFiltersOpen ? 2 : 8,
              mr: defaultFiltersOpen ? 2 : 0,
              minWidth: 350,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.hideBeforeDecember2023}
                  onChange={onHideBeforeDecember2023Change}
                  name="hideBeforeDecember2023"
                  color="primary"
                />
              }
              label="Hide all images before December 31, 2023"
              sx={{ fontFamily: 'Lato' }}
            />
          </FormControl>
        </Box>
      </div>
    </>
  );
}